import { t } from 'i18next';
import DOMPurify from 'dompurify';
import * as css from 'css-tree';

/** Losslessly compact UUIDs to 22 URL-safe characters instead of truncating their identity. */
export const importScope = (guid: string) => {
	const hex = guid.replace(/-/g, '');
	if (/^[a-f\d]{32}$/i.test(hex)) {
		const bytes = hex
			.match(/../g)!
			.map((byte) => String.fromCharCode(parseInt(byte, 16)))
			.join('');
		return `bi-${btoa(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')}`;
	}
	return `bi-x-${Array.from(new TextEncoder().encode(guid), (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
};

const importedClass = (name: string, scope: string) => `${name}-${scope}`;

function decodeCssEscapes(value: string): string {
	return value.replace(/\\([0-9a-f]{1,6}\s?|.)/gi, (_, escaped: string) =>
		/^[0-9a-f]/i.test(escaped) ? String.fromCodePoint(Math.min(parseInt(escaped.trim(), 16), 0x10ffff)) : escaped
	);
}

/** No active documents, custom elements, forms, or script URLs in imported content. */
export function sanitizeImportedHtml(source: string): string {
	const html = DOMPurify.sanitize(source, {
		USE_PROFILES: { html: true, svg: true, svgFilters: true },
		FORBID_TAGS: [
			'script',
			'style',
			'link',
			'meta',
			'base',
			'iframe',
			'object',
			'embed',
			'form',
			'input',
			'textarea',
			'select',
			'foreignObject',
			'animate',
			'animateMotion',
			'animateTransform',
			'set',
		],
		FORBID_ATTR: ['srcdoc', 'autofocus', 'is'],
		ADD_ATTR: ['target'],
	});
	const template = document.createElement('template');
	template.innerHTML = html;
	template.content.querySelectorAll('*').forEach((element) => {
		// Never allow imported markup to impersonate V2/editor wrappers.
		for (const attribute of Array.from(element.attributes)) {
			if (attribute.name.startsWith('data-') || attribute.name.startsWith('on')) element.removeAttribute(attribute.name);
		}
		if (element.hasAttribute('class'))
			element.setAttribute(
				'class',
				(element.getAttribute('class') ?? '')
					.split(/\s+/)
					.filter((name) => !name.startsWith('biz-'))
					.join(' ')
			);
		if (element.hasAttribute('style'))
			element.setAttribute('style', sanitizeDeclarations(element.getAttribute('style') ?? ''));
		if (element.tagName === 'BUTTON') {
			element.setAttribute('type', 'button');
			element.removeAttribute('formaction');
		}
		if (element.getAttribute('target') === '_blank') element.setAttribute('rel', 'noopener noreferrer');
	});
	return template.innerHTML;
}

export function sanitizeDeclarations(source: string): string {
	try {
		const ast = css.parse(source, { context: 'declarationList', parseCustomProperty: true });
		cleanDeclarations(ast);
		return css.generate(ast);
	} catch {
		return '';
	}
}

function cleanDeclarations(ast: css.CssNode) {
	css.walk(ast, {
		visit: 'Declaration',
		enter(node, item, list) {
			let unsafe = /^(behavior|-moz-binding)$/i.test(decodeCssEscapes(node.property));
			css.walk(node.value, (value) => {
				if (
					value.type === 'Raw' ||
					(value.type === 'Function' && /^(expression|paint)$/i.test(decodeCssEscapes(value.name)))
				)
					unsafe = true;
				if (value.type === 'Url' && !isAllowedAssetUrl(value.value)) unsafe = true;
			});
			if (unsafe) list.remove(item);
		},
	});
}

export function isAllowedAssetUrl(value: string): boolean {
	const compact = Array.from(decodeCssEscapes(value))
		.filter((character) => character.charCodeAt(0) > 32)
		.join('');
	return (
		!/^[a-z][a-z\d+.-]*:/i.test(compact) ||
		/^(https?:|blob:|data:image\/(png|jpeg|gif|webp|avif|svg\+xml);base64,)/i.test(compact)
	);
}

/** Parse CSS rather than prefixing strings (commas, nested media and keyframes are structural). */
function registeredProperties(ast: css.CssNode, scope: string): Map<string, string> {
	const names = new Map<string, string>();
	css.walk(ast, (node) => {
		if (node.type === 'Atrule' && node.name.toLowerCase() === 'property' && node.prelude) {
			const name = css.ident.decode(css.generate(node.prelude));
			if (name.startsWith('--')) names.set(name, `--${scope}-${name.slice(2)}`);
		}
	});
	return names;
}

function scopePropertyReferences(ast: css.CssNode, names: Map<string, string>) {
	css.walk(ast, (node) => {
		if (node.type === 'Declaration' && names.has(css.ident.decode(node.property)))
			node.property = css.ident.encode(names.get(css.ident.decode(node.property))!);
		if (node.type === 'Identifier' && names.has(css.ident.decode(node.name)))
			node.name = css.ident.encode(names.get(css.ident.decode(node.name))!);
	});
}

/** Keep animation-specific copies: the same custom property may also be a color or font name. */
function scopeAnimationReferences(ast: css.CssNode, names: Map<string, string>, scope: string) {
	if (!names.size) return;
	const animationProperty = (name: string) => css.ident.encode(`--${scope}-animation-${css.ident.decode(name).slice(2)}`);
	const rewriteValue = (value: css.CssNode) => {
		css.walk(value, (node) => {
			if (node.type === 'Function' && node.name.toLowerCase() === 'var') {
				const children = node.children.toArray();
				const property = children[0];
				if (property?.type !== 'Identifier') return css.walk.skip;
				const fallback: css.Value = { type: 'Value', children: new css.List<css.CssNode>().fromArray(children.slice(2)) };
				rewriteValue(fallback);
				// Fall back to the original for variables supplied by the surrounding portal.
				const replacement = css.parse(
					`var(${animationProperty(property.name)},var(${property.name}${children.length > 1 ? `,${css.generate(fallback)}` : ''}))`,
					{ context: 'value' }
				) as css.Value;
				node.children = (replacement.children.first as css.FunctionNode).children;
				return css.walk.skip;
			}
			if (node.type === 'Identifier' && names.has(node.name)) node.name = names.get(node.name)!;
		});
	};
	css.walk(ast, (node, item, list) => {
		if (node.type === 'Atrule' && node.name.toLowerCase() === 'property' && node.prelude) {
			const copy = css.clone(node) as css.Atrule;
			copy.prelude = css.parse(animationProperty(css.generate(node.prelude)), {
				context: 'atrulePrelude',
				atrule: 'property',
			}) as css.AtrulePrelude;
			css.walk(copy, (child) => {
				if (child.type === 'Declaration' && child.property === 'initial-value') rewriteValue(child.value);
			});
			list.insertData(copy, item);
			return css.walk.skip;
		}
		if (node.type !== 'Declaration') return;
		if (node.property.startsWith('--')) {
			const copy = css.clone(node) as css.Declaration;
			copy.property = animationProperty(node.property);
			rewriteValue(copy.value);
			list.insertData(copy, item);
		} else if (/^(?:-webkit-)?animation(?:-name)?$/i.test(node.property)) {
			rewriteValue(node.value);
		}
		return css.walk.skip;
	});
}

export function scopeImportedCss(source: string, scope: string): { css: string; warnings: string[] } {
	const warnings = new Set<string>();
	try {
		const ast = css.parse(source, { parseCustomProperty: true });
		cleanDeclarations(ast);
		scopePropertyReferences(ast, registeredProperties(ast, scope));
		css.walk(ast, (node) => {
			if (node.type === 'Layer') {
				// Rename each path component so nested layers and dotted ordering declarations agree.
				node.name = node.name
					.split(/(?<!\\)\./)
					.map((name) => `${scope}-${name}`)
					.join('.');
			}
		});
		const names = new Map<string, string>();
		css.walk(ast, {
			visit: 'Atrule',
			enter(node, item, list) {
				if (/^(keyframes|-webkit-keyframes)$/i.test(node.name) && node.prelude) {
					const name = css.generate(node.prelude);
					names.set(name, `${scope}-${name.replace(/[^a-zA-Z0-9_-]/g, '')}`);
				} else if (!/^(media|supports|layer|property)$/i.test(node.name)) {
					warnings.add(t('HTML_IMPORT.CSS_AT_RULE', { name: node.name }));
					list.remove(item);
				}
			},
		});
		css.walk(ast, {
			visit: 'Atrule',
			enter(node) {
				if (/keyframes$/i.test(node.name) && node.prelude) {
					node.prelude = css.parse(names.get(css.generate(node.prelude))!, {
						context: 'atrulePrelude',
						atrule: node.name,
					}) as css.AtrulePrelude;
				}
			},
		});
		scopeAnimationReferences(ast, names, scope);
		css.walk(ast, {
			visit: 'Rule',
			enter(node, item, list) {
				if (this.atrule && /keyframes$/i.test(this.atrule.name)) return;
				if (node.prelude.type !== 'SelectorList') {
					list.remove(item);
					warnings.add(t('HTML_IMPORT.CSS_SELECTOR'));
					return;
				}
				const selectors: string[] = [];
				node.prelude.children.forEach((selector) => {
					let hasRoot = false;
					css.walk(selector, (part, entry, parent) => {
						if (part.type === 'ClassSelector')
							part.name = css.ident.encode(importedClass(css.ident.decode(part.name), scope));
						// Attribute comparisons use the original class list, including substring/word matches.
						if (part.type === 'AttributeSelector' && css.ident.decode(part.name.name).toLowerCase() === 'class')
							part.name.name = 'data-biz-import-class';
						if (part.type === 'AttributeSelector' && css.ident.decode(part.name.name).toLowerCase() === 'id')
							part.name.name = 'data-biz-import-anchor';
						if (part.type === 'TypeSelector' && part.name.toLowerCase() === 'main') {
							const mainSelector = css.parse('[data-biz-import-main]', { context: 'selector' }) as css.Selector;
							parent.replace(entry, parent.createItem(mainSelector.children.first!));
						}
						if (part.type === 'IdSelector') part.name = `${scope}-${part.name}`;
						if (
							(part.type === 'TypeSelector' && /^(html|body)$/i.test(part.name)) ||
							(part.type === 'PseudoClassSelector' &&
								(part.name === 'root' || (part.name === 'host' && !part.children)))
						) {
							hasRoot = true;
							parent.replace(entry, parent.createItem({ type: 'ClassSelector', name: scope }));
						}
					});
					let text = css.generate(selector);
					// html body collapses to the single group root.
					text = text.replace(new RegExp(`\\.${scope}\\s+(?:>\\s*)?\\.${scope}`, 'g'), `.${scope}`);
					const scoped = hasRoot && text.startsWith(`.${scope}`) ? text : `.${scope} ${text}`;
					// Constrain the subject as well: body + .portal must never select outside the group.
					const safeSelector = css.parse(scoped, { context: 'selector' }) as css.Selector;
					const guard = css.parse(
						`:where(.${scope},.${scope} [data-biz-import-element]):not(.${scope} .biz-import-group,.${scope} .biz-import-group *)`,
						{ context: 'selector' }
					) as css.Selector;
					let pseudoElement: css.ListItem<css.CssNode> | undefined;
					safeSelector.children.forEach((part, entry) => {
						if (
							part.type === 'PseudoElementSelector' ||
							(part.type === 'PseudoClassSelector' && /^(before|after|first-line|first-letter)$/.test(part.name))
						)
							pseudoElement = entry;
					});
					if (pseudoElement) safeSelector.children.insertList(guard.children, pseudoElement);
					else safeSelector.children.appendList(guard.children);
					selectors.push(css.generate(safeSelector));
				});
				node.prelude = css.parse(selectors.join(','), { context: 'selectorList' }) as css.SelectorList;
			},
		});
		css.walk(ast, (node) => {
			if (node.type === 'Url' && node.value.startsWith('#')) node.value = `#${scope}-${node.value.slice(1)}`;
		});
		// Unlayered portal/admin rules otherwise outrank every normal imported layer.
		// Only source elements get this reset, never V2 wrappers or editor controls.
		// Imported unlayered rules follow with at least the same specificity; inline styles win.
		const sourceReset = `.${scope} :where([data-biz-import-element]):not(.${scope} .biz-import-group,.${scope} .biz-import-group *){all:revert-layer}`;
		return { css: sourceReset + css.generate(ast), warnings: [...warnings] };
	} catch {
		return { css: '', warnings: [t('HTML_IMPORT.CSS_PARSE')] };
	}
}

/** IDs are unique per imported group, while fragment links still work between its sections. */
export function scopeImportedMarkup(html: string, scope: string, stylesheet = '', publicAnchors = true): string {
	const animations = new Map<string, string>();
	let properties = new Map<string, string>();
	try {
		const stylesheetAst = css.parse(stylesheet, { parseCustomProperty: true });
		properties = registeredProperties(stylesheetAst, scope);
		css.walk(stylesheetAst, (node) => {
			if (node.type === 'Atrule' && /keyframes$/i.test(node.name) && node.prelude) {
				const name = css.generate(node.prelude);
				animations.set(name, `${scope}-${name.replace(/[^a-zA-Z0-9_-]/g, '')}`);
			}
		});
	} catch {
		/* Invalid CSS is reported by the CSS editor/import preview. */
	}
	const template = document.createElement('template');
	template.innerHTML = sanitizeImportedHtml(html);
	// Preserve main's selector context without introducing additional page landmarks.
	template.content.querySelectorAll('main').forEach((main) => {
		const replacement = document.createElement('div');
		for (const attribute of Array.from(main.attributes)) replacement.setAttribute(attribute.name, attribute.value);
		replacement.setAttribute('data-biz-import-main', '');
		replacement.append(...Array.from(main.childNodes));
		main.replaceWith(replacement);
	});
	template.content.querySelectorAll('*').forEach((element) => {
		element.setAttribute('data-biz-import-element', '');
		if (element.hasAttribute('class')) {
			element.setAttribute('data-biz-import-class', element.getAttribute('class') ?? '');
			element.setAttribute(
				'class',
				(element.getAttribute('class') ?? '').replace(/[^\t\n\f\r ]+/g, (name) => importedClass(name, scope))
			);
		}
		if (element.id) {
			element.setAttribute('data-biz-import-anchor', element.id);
			element.id = `${scope}-${element.id}`;
		}
		for (const name of ['href', 'xlink:href']) {
			const value = element.getAttribute(name);
			// Navigation keeps the author's public fragment; SVG resource references stay scoped.
			if (publicAnchors && element.localName === 'a') continue;
			if (value?.startsWith('#') && value.length > 1) element.setAttribute(name, `#${scope}-${value.slice(1)}`);
		}
		for (const name of [
			'aria-labelledby',
			'aria-describedby',
			'aria-controls',
			'aria-owns',
			'aria-activedescendant',
			'aria-details',
			'aria-errormessage',
			'aria-flowto',
			'headers',
			'for',
			'list',
			'form',
			'itemref',
			'popovertarget',
			'commandfor',
		]) {
			const value = element.getAttribute(name);
			if (value)
				element.setAttribute(
					name,
					value
						.split(/\s+/)
						.map((id) => `${scope}-${id}`)
						.join(' ')
				);
		}
		for (const attribute of [
			'style',
			'fill',
			'stroke',
			'filter',
			'clip-path',
			'mask',
			'marker-start',
			'marker-mid',
			'marker-end',
		]) {
			const value = element.getAttribute(attribute);
			if (!value) continue;
			try {
				const ast = css.parse(value, {
					context: attribute === 'style' ? 'declarationList' : 'value',
					parseCustomProperty: true,
				});
				scopePropertyReferences(ast, properties);
				scopeAnimationReferences(ast, animations, scope);
				css.walk(ast, function (node) {
					if (node.type === 'Url' && node.value.startsWith('#')) node.value = `#${scope}-${node.value.slice(1)}`;
				});
				element.setAttribute(attribute, css.generate(ast));
			} catch {
				element.removeAttribute(attribute);
			}
		}
	});
	return template.innerHTML;
}
